import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '~env/environment';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import {
  GtmLoggedInTypeEnum,
  GtmSalesLineEnum,
  GtmSolutionEnum,
  GtmTemplateTypeEnum,
  GtmUserTypeEnum,
} from '~shared/modules/ga-tracking/constants';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { getLoginType } from '~server/utils/auth.utils';
import { METRO_COOKIE_NAME } from '~shared/constants/buyer-cookie.constant';
import { CookieService } from '~shared/services/cookie.service';

const GTM_DATALAYER_SCRIPT_CONTAINER = 'gtm-datalayer-script-container';

@Injectable({
  providedIn: 'root',
})
export class GtmDatalayerService {
  private gtmParams = environment.gaGtmParams();
  private ga4GtmParams = environment.ga4GtmParams();
  private eventsBatch: any = [];

  constructor(
    private identityService: IdentityService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private cookieService: CookieService
  ) {}

  public initDataLayer() {
    this.loadScript(environment.gaTrackingId(), this.gtmParams);
    this.loadGa4Script(environment.ga4TrackingId(), this.ga4GtmParams);
    this.loadNoScriptContainer(environment.gaTrackingId(), this.gtmParams);
    this.loadNoScriptContainer(environment.ga4TrackingId(), this.ga4GtmParams);
  }

  public getDataLayerConfig(event) {
    const userData = this.identityService.getUserInformation();
    const loggedIn = userData.loggedIn;

    const hashedIds = {
      GA360_userId: userData?.hashedIdamUserId,
      hashedPersonId: userData?.hashedPersonCdmId,
      hashedActiveContext: userData?.hashedActiveContextId,
      hashedEmail: userData?.hashedEmail,
    };

    return {
      GA360_userType: loggedIn ? GtmUserTypeEnum.LoggedId : GtmUserTypeEnum.NotLoggedId,
      GA360_loggedIn: loggedIn ? GtmLoggedInTypeEnum.LoggedId : GtmLoggedInTypeEnum.NotLoggedId,
      GA360_solution: this.getSolution(),
      GA360_salesLine: this.getSalesLine(),
      GA360_country: this.getCountry(),
      GA360_errorPage: 'no-error',
      GA360_statusCode: 'none',
      GA360_pageName: this.getGtmPageName(event),
      GA360_pageTitle: this.getGtmPageTitle(event),
      GA360_templateType: this.getGtmTemplateType(event),
      GA360_loginType: getLoginType(),
      ...(event.tier ? { GA360_customerTier: event.tier } : {}),
      ...(loggedIn && hashedIds),
    };
  }
  public reportPendingGTMEvents(): void {
    this.eventsBatch.forEach((event) => {
      this.pushDataLayer(event);
    });
    this.eventsBatch = [];
  }
  public pushDataLayer(event: any) {
    const isCookieAccepted = this.cookieService.get(METRO_COOKIE_NAME)?.includes?.('|performance|');
    const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || []);
    if (!isCookieAccepted) {
      this.eventsBatch.push(event);
      return;
    }

    dataLayer.push(event);
  }

  public gtagEvent(event: any): void {
    const GA_360 = this.getDataLayerConfig(event);

    event = { ...GA_360, ...event };

    if (!event['category'] && !event['ecommerce']) {
      event = { category: undefined, ...event };
    }

    if (!event['label'] && !event['ecommerce']) {
      event = { label: undefined, ...event };
    }

    if (!event['content-name']) {
      event = { 'content-name': this.getContentName(), ...event };
    }

    this.pushDataLayer(event || arguments);
  }

  public gtagECommerceClearEvent() {
    this.pushDataLayer({ ecommerce: null });
  }

  private loadScript(gtmId, gtmParams = '') {
    const node = this.document.createElement('script');

    node.innerText =
      `(function(w,d,s,l,i){w[l]=w[l]||[];` +
      `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;` +
      `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl+'${gtmParams}';f.parentNode.insertBefore(j,f);})` +
      `(window,document,'script','dataLayer','${[gtmId]}');`;

    const gtmDataLayerScriptContainer = document.getElementById(GTM_DATALAYER_SCRIPT_CONTAINER);
    gtmDataLayerScriptContainer.parentNode.insertBefore(node, gtmDataLayerScriptContainer.nextSibling);
  }

  private loadGa4Script(gtmId, gtmParams = '') {
    const node = this.document.createElement('script');
    node.innerText =
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});` +
      `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;` +
      `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl+'${gtmParams}';f.parentNode.insertBefore(j,f);})` +
      `(window,document,'script','dataLayer','${[gtmId]}');`;

    const gtmDataLayerScriptContainer = document.getElementById(GTM_DATALAYER_SCRIPT_CONTAINER);
    gtmDataLayerScriptContainer.parentNode.insertBefore(node, gtmDataLayerScriptContainer.nextSibling);
  }

  private loadNoScriptContainer(gtmId, gtmParams = '') {
    const node = this.document.createElement('noscript');
    const iframe = this.document.createElement('iframe');
    const targetContainer = this.document.getElementsByTagName('body')[0];
    const firstChild = targetContainer.firstChild;

    iframe.setAttribute(`src`, `//www.googletagmanager.com/ns.html?id=${[gtmId]}${gtmParams}`);
    iframe.setAttribute(`title`, `IframeGTM`);
    iframe.setAttribute(`height`, `0`);
    iframe.setAttribute(`width`, `0`);
    iframe.setAttribute(`style`, `display:none; visibility:hidden;`);
    node.appendChild(iframe);

    targetContainer.insertBefore(node, firstChild);
  }

  private getSolution() {
    return GtmSolutionEnum.MM_marketplace;
  }

  private getCountry() {
    return (environment.countryDomainCode() as string).toUpperCase();
  }

  private getGtmPageName(event) {
    const pageTitleParts = [];
    const pageTitle = this.getPageTitle(event);
    if (pageTitle) {
      pageTitleParts.push(pageTitle);
    }
    pageTitleParts.push(this.getCountry());
    pageTitleParts.push(this.getSolution());
    const categoryNames = event.getBreadcrumbsCategoryNames ? event.getBreadcrumbsCategoryNames() : [];
    if (categoryNames.length > 0) {
      pageTitleParts.push(categoryNames.join('|').toLowerCase());
    }

    return pageTitleParts.join('|');
  }

  // single property pageType
  private getPageTitle(event) {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    if (event.getGtmDataLayerTitle) {
      return event.getGtmDataLayerTitle()?.toString()?.toLowerCase();
    } else {
      const head = this.document.getElementsByTagName('head');
      const title = head.length > 0 ? head[0].getElementsByTagName('title') : null;
      let titleParts = [];
      if (title.length > 0) {
        titleParts = title[0].innerText.toLowerCase().split('|');
        return titleParts.length > 1 ? titleParts.slice(0, -1).join('|').trim() : titleParts[0].trim();
      } else {
        return '';
      }
    }
  }

  private getContentName(): string {
    const url = this.router.url;
    return `${environment.appBaseUrl()}${url}`;
  }

  // pageType value for GTM dataLayer
  private getGtmPageTitle(event) {
    const pageTitleParts = [];
    const pageTitle = this.getPageTitle(event);
    if (pageTitle) {
      pageTitleParts.push(pageTitle);
    }
    const eventTemplateType = this.getGtmTemplateType(event);
    if (eventTemplateType.length > 0) {
      pageTitleParts.push(eventTemplateType);
    }

    return pageTitleParts.join('|').toLowerCase();
  }

  private getGtmTemplateType(event) {
    return event.getGtmDataLayerTemplateType
      ? event.getGtmDataLayerTemplateType().toLowerCase()
      : GtmTemplateTypeEnum.Other;
  }

  private getSalesLine() {
    return GtmSalesLineEnum[this.getCountry()];
  }
}
